import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { shortAddress } from "@tenergy/web-core/format";
import { tronNetwork } from "@tenergy/web-core/wallet";
import type { LedgerAccount } from "./auth/ledger.js";
import { SHOW_LEDGER_TRON } from "./auth/ledger-flags.js";
import { rememberedAdminAddress } from "./auth/session-store.js";
import { Button, Icon, Note, inputClass } from "./ui.js";
import type { AdminSessionValue, SignInWay } from "./session/use-admin-session.js";

const FAILURE_TEXT: Record<string, string> = {
  locked: "The wallet is locked. Unlock it and try again.",
  rejected: "The signature was declined.",
  refused: "This sign-in did not check out: the address has no admin access, it is frozen, or the signature was for another text.",
  unavailable: "No wallet was found in this browser.",
  closed: "The wallet connection was closed before it finished.",
  device: "The Ledger did not finish.",
  offline: "The API did not answer.",
};

const isSame = (address: string, other: string | null) => other !== null && address.toLowerCase() === other.toLowerCase();

/** The account signed in with last goes first, the rest keep Ledger Live's order. */
function lastUsedFirst(accounts: LedgerAccount[], last: string | null): LedgerAccount[] {
  return [...accounts.filter((a) => isSame(a.address, last)), ...accounts.filter((a) => !isSame(a.address, last))];
}

/**
 * Which Ledger account signs in: a dialog over the sign-in screen once the device is connected (owner 2026-10-31: in
 * a popup, not on the page). The last used account goes first; Esc, a click outside or Cancel closes it.
 */
function AccountPicker({
  accounts,
  reading,
  error,
  lastUsed,
  session,
}: {
  accounts: LedgerAccount[];
  reading: boolean;
  error: string | null;
  lastUsed: string | null;
  session: AdminSessionValue;
}) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [typingPath, setTypingPath] = useState(false);
  // Prefilled with the app's account-1 path, the usual starting point for a nearby one.
  const firstPath = accounts[0]?.app === "tron" ? "44'/195'/0'/0/0" : "44'/60'/0'/0/0";
  const [path, setPath] = useState(firstPath);
  const pathRef = useRef<HTMLInputElement | null>(null);
  const cancel = session.cancel;
  useEffect(() => {
    dialogRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") cancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [cancel]);

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) session.cancel();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-picker-title"
        class="w-full max-w-[380px] rounded-[20px] border border-line bg-surface p-5 outline-none"
      >
        <h2 id="account-picker-title" class="font-display text-[16px] font-extrabold tracking-[-0.01em] text-ink">
          Which account signs in?
        </h2>
        <p class="mt-1 text-[11.5px] text-ink-muted">Numbered as in Ledger Live.</p>
        {/* Five rows fit as they are; past five ("Show 5 more"), the list scrolls (owner 2026-10-31). */}
        <ul class="mt-3 max-h-[232px] space-y-1.5 overflow-y-auto pr-1">
          {lastUsedFirst(accounts, lastUsed).map((account) => {
            const last = isSame(account.address, lastUsed);
            return (
              <li key={account.path}>
                <button
                  type="button"
                  title={`${account.address}\n${account.path}`}
                  disabled={reading}
                  onClick={() => void session.chooseAccount(account)}
                  class={`flex w-full items-center gap-2.5 rounded-[10px] border px-3 py-2.5 text-left transition-colors hover:border-accent/60 hover:bg-surface-raised ${
                    last ? "border-accent/60" : "border-line"
                  }`}
                >
                  <span class="text-[11.5px] whitespace-nowrap text-ink-muted">
                    {account.scheme === "custom" ? `Path ${account.path}` : `Account ${account.number}`}
                  </span>
                  <span class="num text-[12.5px] text-ink">{shortAddress(account.address)}</span>
                  {last ? <span class="ml-auto text-[10.5px] text-accent-text">last used</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
        <button
          type="button"
          disabled={reading}
          onClick={() => void session.moreAccounts()}
          class="mt-1.5 w-full rounded-[10px] border border-dashed border-line px-3 py-2 text-[11.5px] text-ink-muted transition-colors hover:border-accent/60 hover:text-ink"
        >
          {reading ? "Reading the Ledger…" : "Show 5 more"}
        </button>

        {typingPath ? (
          <form
            class="mt-3 flex gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (path.trim() !== "") void session.accountAtPath(path);
            }}
          >
            <div class="relative min-w-0 flex-1">
              <input
                ref={pathRef}
                class={`${inputClass} num w-full pr-8`}
                aria-label="Derivation path"
                placeholder={firstPath}
                value={path}
                onInput={(event) => setPath((event.currentTarget as HTMLInputElement).value)}
                spellcheck={false}
                autoFocus
              />
              {path === "" ? null : (
                <button
                  type="button"
                  aria-label="Clear the path"
                  onClick={() => {
                    setPath("");
                    pathRef.current?.focus();
                  }}
                  class="absolute top-1/2 right-2 flex size-5 -translate-y-1/2 items-center justify-center rounded-full text-[14px] leading-none text-ink-muted hover:bg-surface-raised hover:text-ink"
                >
                  ×
                </button>
              )}
            </div>
            <Button type="submit" disabled={reading || path.trim() === ""}>
              Read
            </Button>
          </form>
        ) : null}
        {error === null ? null : (
          <div class="mt-3">
            <Note tone="danger">{error}</Note>
          </div>
        )}

        <div class="mt-4 flex items-center justify-between gap-2">
          {typingPath ? (
            <span />
          ) : (
            <Button tone="ghost" onClick={() => setTypingPath(true)}>
              Enter path
            </Button>
          )}
          <Button tone="ghost" onClick={session.cancel}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

/** What this browser can reach a Ledger by; read once, it does not change while the page is open. */
function ledgerSupport(): { usb: boolean; ble: boolean } {
  const nav = typeof navigator === "undefined" ? ({} as Navigator) : navigator;
  return { usb: "hid" in nav, ble: "bluetooth" in nav };
}

function Way({
  title,
  hint,
  safety,
  children,
}: {
  title: string;
  hint: string;
  safety: "safest" | "less-safe";
  children: ComponentChildren;
}) {
  return (
    <div class="rounded-[14px] border border-line bg-surface-2/40 p-3">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <h2 class="text-[13px] font-semibold text-ink">{title}</h2>
        <span
          class={`rounded-full px-2 py-0.5 text-[10.5px] font-medium ${
            safety === "safest" ? "bg-save/15 text-save" : "bg-heat/12 text-warning"
          }`}
        >
          {safety === "safest" ? "Safest" : "Less safe"}
        </span>
      </div>
      <p class="mt-1 text-[11px] leading-relaxed text-ink-muted">{hint}</p>
      <div class="mt-2.5 flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

/**
 * The admin sign-in. Its own, not the dashboard's (docs/product/admin-auth.md §2): every way proves the address by a
 * signature over the panel's own text, and the session it opens is bound to this browser's key.
 *
 * The ways are ranked by what the admin can see on the device (owner 2026-10-31): a Ledger with the Ethereum app
 * shows the very text being signed; the others are marked less safe.
 */
export function SignInScreen({
  session,
  network = tronNetwork().label,
  embedded = false,
}: {
  session: AdminSessionValue;
  network?: string;
  /** Inside a page (the Nile tab) rather than the whole screen. */
  embedded?: boolean;
}) {
  const phase = session.phase;
  const busy = phase.kind === "connecting" || phase.kind === "signing";
  const passkeyAddress = rememberedAdminAddress();
  const support = ledgerSupport();
  const go = (way: SignInWay) => void session.signIn(way);
  const [typedAddress, setTypedAddress] = useState("");
  // Only the ways this browser has: no mobile browser has WebHID (USB), and iOS has neither (owner 2026-10-31: a
  // dead USB button on Android said nothing). With neither, the block says so instead of offering buttons.
  const transports = (app: "ethereum" | "tron") =>
    !support.usb && !support.ble ? (
      <span class="text-[10.5px] text-ink-muted">Not available in this browser: use Chrome or Edge on a computer, or Chrome on Android.</span>
    ) : (
      <>
        {support.usb ? (
          <Button disabled={busy} onClick={() => go({ kind: "ledger", app, transport: "usb" })}>
            USB
          </Button>
        ) : null}
        {support.ble ? (
          <Button tone={support.usb ? "ghost" : "primary"} disabled={busy} onClick={() => go({ kind: "ledger", app, transport: "ble" })}>
            Bluetooth
          </Button>
        ) : null}
      </>
    );

  const Frame = embedded ? "div" : "main";
  return (
    <Frame
      {...(embedded ? {} : { id: "main" })}
      class={embedded ? "flex justify-center py-4" : "flex min-h-screen items-center justify-center px-4 py-12"}
    >
      <div class="w-full max-w-[460px] rounded-[20px] border border-line bg-surface p-6 sm:p-7">
        <div class="text-center">
          <span class="mx-auto flex size-12 items-center justify-center rounded-[14px] bg-accent-soft text-accent-text">
            <Icon name="shield" />
          </span>
          <h1 class="mt-4 font-display text-[22px] font-extrabold tracking-[-0.02em] text-ink">Platform admin</h1>
          <p class="mt-2 text-[12px] leading-relaxed text-ink-muted">
            Internal tool. Sign in with an address the owner or an admin has added. Signing is free and is not a
            transaction.
          </p>
        </div>

        <div class="mt-5 space-y-3">
          {phase.kind === "failed" ? (
            <Note tone="danger">
              {phase.reason === "wrong_network"
                ? `This wallet is on the wrong network. Switch it to ${network}.`
                : (FAILURE_TEXT[phase.reason] ?? FAILURE_TEXT["offline"])}
              {phase.message !== null ? ` ${phase.message}` : ""}
            </Note>
          ) : null}
          {busy ? (
            <Note>{phase.kind === "connecting" ? "Connecting…" : "Confirm on the device or in the wallet…"}</Note>
          ) : null}

          {phase.kind === "choose-account" ? (
            <AccountPicker
              accounts={phase.accounts}
              reading={phase.reading}
              error={phase.error}
              lastUsed={passkeyAddress}
              session={session}
            />
          ) : null}

          <Way
            title="Ledger · Ethereum app"
            safety="safest"
            hint="The Ledger screen shows the exact sign-in text before you approve it."
          >
            {transports("ethereum")}
          </Way>

          {SHOW_LEDGER_TRON ? (
            <Way
              title="Ledger · TRON app"
              safety="less-safe"
              hint='The Ledger shows only a hash of the text. Needs the TRON app setting "Sign by hash".'
            >
              {transports("tron")}
            </Way>
          ) : null}

          <Way title="TRON wallet" safety="less-safe" hint="TronLink or WalletConnect: the wallet signs on this computer or phone.">
            <Button disabled={busy} onClick={() => go({ kind: "tron-wallet" })}>
              Connect wallet
            </Button>
          </Way>

          <Way
            title="Passkey"
            safety="less-safe"
            hint="A security key bound to your address, equal to its signature. Add one under Security after a wallet sign-in."
          >
            {passkeyAddress === null ? (
              // A browser that has never signed in here does not know the address the key is bound to (Ledger keys
              // are found by address, not discovered), so it is asked once — on a new phone, say (owner 2026-10-31).
              <form
                class="flex w-full gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (typedAddress.trim() !== "") go({ kind: "passkey", address: typedAddress.trim() });
                }}
              >
                <input
                  class={`${inputClass} num min-w-0 flex-1`}
                  aria-label="Your admin address"
                  placeholder="0x… or T…"
                  value={typedAddress}
                  onInput={(event) => setTypedAddress((event.currentTarget as HTMLInputElement).value)}
                  autocomplete="username webauthn"
                  spellcheck={false}
                />
                <Button type="submit" disabled={busy || typedAddress.trim() === ""}>
                  Use passkey
                </Button>
              </form>
            ) : (
              <Button disabled={busy} onClick={() => go({ kind: "passkey", address: passkeyAddress })}>
                Use passkey
              </Button>
            )}
          </Way>

        </div>
        <p class="num mt-5 text-center text-[10.5px] text-ink-muted">Admin sign-in · {network}</p>
      </div>
    </Frame>
  );
}
